"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, Suspense } from "react";
import { HOME_NAV_ITEM, NAV_GROUPS, NavItem } from "./nav-config";
import { resolveActiveHref } from "./nav-active";
import { cn } from "@/lib/utils";
import { ChevronDown, Lock, PanelLeftClose, PanelLeftOpen, Search, X } from "lucide-react";
import { SheetClose } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { useNavigationLoading } from "./navigation-loading-context";
import { HelpMenu } from "./HelpMenu";
import { RailUtilities } from "./RailUtilities";
import { openCommandPalette } from "./CommandPaletteMount";
import { railButtonClass } from "./rail-item";
import { UserMenu } from "./UserMenu";
import { LiveBroadcastHeader } from "./LiveBroadcastHeader";
import { OnboardingChecklist } from "@/components/onboarding/onboarding-checklist";
import { toolLoadNavigationPrefetch } from "@/lib/performance/tool-load-timing";
import { useToolLoadNavPrefetchAttestation } from "./use-tool-load-nav-prefetch-attestation";
import { useLockedToolTier } from "./use-locked-tool-tier";
import { BUNDLE_TIER_NAME } from "@/lib/billing/purchase-route";
import type { BundleTierId } from "@/lib/billing/bundle-entitlements";
import { phCapture } from "@/utils/analytics";

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  className?: string;
  hasOnboardingPreferences?: boolean;
  checklistDismissed?: boolean;
  variant?: "desktop" | "mobile";
  showOnboardingChecklist?: boolean;
  hasUnreadUpdates?: boolean;
}


interface ItemProps {
  item: NavItem;
  activeHref: string | null;
  collapsed: boolean;
  isMobile: boolean;
  expanded: Record<string, boolean>;
  onToggleExpand: (href: string) => void;
  navigatingTo: string | null;
  startNavigation: (href: string) => void;
  onNavigate?: () => void;
  navigationPrefetch: false | undefined;
  navigationPrefetchAttestation: "disabled" | "default";
  lockedTierFor: (href: string) => BundleTierId | null;
}

function Item({
  item,
  activeHref,
  collapsed,
  isMobile,
  expanded,
  onToggleExpand,
  navigatingTo,
  startNavigation,
  onNavigate,
  navigationPrefetch,
  navigationPrefetchAttestation,
  lockedTierFor,
}: ItemProps) {
  const active = item.href === activeHref;
  const lockedTier = lockedTierFor(item.href);
  const lockedLabel = lockedTier ? `Comes with ${BUNDLE_TIER_NAME[lockedTier]}` : undefined;
  const hasActiveChild = item.children?.some((c) => c.href === activeHref) ?? false;
  const Icon = item.icon;
  const isThisNavigating = navigatingTo === item.href;
  const hasChildren = !collapsed && !!item.children?.length;

  const isExpanded = isMobile
    ? (hasActiveChild ? true : (expanded[item.href] ?? active))
    : (active || hasActiveChild);

  const showActive = active || (hasActiveChild && !(hasChildren && isExpanded));

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    if (lockedTier) phCapture("nav_locked_tool_clicked", { href: item.href, tier: lockedTier, surface: isMobile ? "mobile_menu" : "sidebar" });
    if (!active) {
      startNavigation(item.href);
    }
    onNavigate?.();
  };

  return (
    <div>
      <div className="relative flex items-center">
        <Link
          href={item.href}
          prefetch={navigationPrefetch}
          data-tool-nav-prefetch={navigationPrefetchAttestation}
          onClick={handleClick}
          aria-current={showActive ? "page" : undefined}
          title={collapsed ? (lockedLabel ? `${item.label} · ${lockedLabel}` : item.label) : lockedLabel}
          className={cn(
            railButtonClass(collapsed, isMobile, { quiet: !showActive }),
            "flex-1",
            showActive && (isMobile ? "bg-primary/10 text-primary" : "bg-white/[0.08] text-white"),
            isThisNavigating && "pointer-events-none opacity-70"
          )}
        >
          {showActive && !isMobile && (
            <span className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />
          )}
          {Icon && (
            isThisNavigating ? (
              <Spinner size="sm" className={cn(
                "shrink-0 text-primary",
                isMobile ? "size-[18px]" : "size-4"
              )} />
            ) : (
              <span className="relative shrink-0">
                <Icon className={cn(
                  "shrink-0 transition-colors",
                  isMobile ? "size-[18px]" : "size-4",
                  showActive
                    ? "text-primary"
                    : isMobile
                      ? "text-zinc-500"
                      : "text-foreground/50 group-hover:text-foreground/80"
                )} />
                {lockedTier && collapsed && !isMobile && (
                  <Lock aria-hidden="true" data-testid="nav-lock" className="absolute -right-1.5 -top-1.5 size-2.5 rounded-full bg-surface-void text-foreground/60" />
                )}
              </span>
            )
          )}
          {collapsed && !isMobile ? (
            <span className="max-w-full truncate text-center text-[10px] font-medium leading-none text-foreground/60 group-hover:text-foreground/80">
              {item.shortLabel ?? item.label}
            </span>
          ) : (
            <span className={cn(
              "font-medium truncate min-w-0",
              isMobile ? "text-[14px]" : "text-[15px] md:text-[13px]"
            )}>{item.label}</span>
          )}
          {lockedTier && (isMobile || !collapsed) && (
            <Lock aria-hidden="true" data-testid="nav-lock" className={cn(
              "ml-auto shrink-0 text-foreground/55 group-hover:text-foreground/80",
              isMobile ? "size-4" : "size-3.5"
            )} />
          )}
          {lockedLabel && <span className="sr-only">{`, locked. ${lockedLabel}`}</span>}
        </Link>

        {isMobile && hasChildren && (
          <button
            type="button"
            onClick={() => onToggleExpand(item.href)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
            className={cn(
              "flex items-center justify-center size-11 shrink-0 rounded-md",
              "text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]",
              "transition-all duration-150",
              "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary/40"
            )}
          >
            <ChevronDown className={cn(
              "size-4 transition-transform duration-200",
              isExpanded && "rotate-180"
            )} />
          </button>
        )}
      </div>

      {hasChildren && isExpanded ? (
        <div className={cn(
          "border-l border-white/10 space-y-0.5",
          isMobile
            ? "mt-0.5 ml-[19px] pl-2.5"
            : "mt-0.5 ml-[26px] pl-2.5"
        )}>
          {item.children!.map((c) => {
            const childActive = c.href === activeHref;
            return (
              <Link
                key={c.href}
                href={c.href}
                prefetch={navigationPrefetch}
                data-tool-nav-prefetch={navigationPrefetchAttestation}
                onClick={(event) => {
                  if (
                    !event.defaultPrevented &&
                    event.button === 0 &&
                    !event.metaKey &&
                    !event.ctrlKey &&
                    !event.shiftKey &&
                    !event.altKey &&
                    !childActive
                  ) {
                    startNavigation(c.href);
                  }
                  onNavigate?.();
                }}
                aria-current={childActive ? "page" : undefined}
                className={cn(
                  "block rounded transition-colors",
                  "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary/40",
                  isMobile
                    ? "flex min-h-11 items-center rounded-md px-2.5 text-[13px] font-medium"
                    : "text-[13px] md:text-[12px] py-1.5 px-2",
                  childActive
                    ? isMobile ? "bg-primary/10 text-primary" : "text-primary bg-white/[0.06]"
                    : isMobile
                      ? "text-zinc-400 hover:bg-white/[0.04]"
                      : "text-foreground/50 hover:text-foreground/80 hover:bg-white/[0.03]"
                )}
              >
                {c.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function GroupHeader({
  label,
  isFirst,
  collapsed,
  isMobile,
}: {
  label: string;
  isFirst: boolean;
  collapsed: boolean;
  isMobile: boolean;
}) {
  if (collapsed) {
    return <div className={cn("mx-auto w-8 border-t border-white/[0.08]", isFirst ? "mt-0" : "mt-3 mb-2")} />;
  }
  return (
    <div className={cn(
      "px-2.5",
      isFirst ? "mb-1.5" : isMobile ? "mt-5 mb-1" : "mt-4 mb-1.5"
    )}>
      <span className={cn(
        "font-medium uppercase tracking-[0.14em] text-[10px]",
        isMobile ? "text-zinc-500" : "data-num text-muted-foreground"
      )}>
        {label}
      </span>
    </div>
  );
}

export default function Sidebar({
  collapsed = false,
  onToggle,
  onNavigate,
  className,
  hasOnboardingPreferences = false,
  checklistDismissed = false,
  variant = "desktop",
  showOnboardingChecklist = true,
  hasUnreadUpdates = false,
}: SidebarProps) {
  const pathname = usePathname();
  const { navigatingTo, startNavigation } = useNavigationLoading();
  const isMobile = variant === "mobile";
  const navigationPrefetch = toolLoadNavigationPrefetch();
  const navigationPrefetchAttestation = useToolLoadNavPrefetchAttestation(navigationPrefetch);
  const lockedTierFor = useLockedToolTier();

  const activeHref = resolveActiveHref(
    pathname,
    [
      HOME_NAV_ITEM.href,
      ...NAV_GROUPS.flatMap((g) =>
        g.items.flatMap((i) => [i.href, ...(i.children?.map((c) => c.href) ?? [])])
      ),
    ]
  );

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = (href: string) => {
    setExpanded((prev) => ({ ...prev, [href]: !prev[href] }));
  };

  const itemProps = {
    activeHref,
    collapsed,
    isMobile,
    expanded,
    onToggleExpand: toggleExpand,
    navigatingTo,
    startNavigation,
    onNavigate,
    navigationPrefetch,
    navigationPrefetchAttestation,
    lockedTierFor,
  };

  return (
    <div
      className={cn(
        "h-full shrink-0 overflow-hidden transition-all duration-200",
        "flex flex-col bg-surface-void",
        !isMobile && "border-r border-white/[0.08]",
        !isMobile && (collapsed ? "w-20" : "w-56"),
        className
      )}
    >
      <div
        className={cn(
          "flex h-12 shrink-0 items-center border-b border-white/[0.08]",
          isMobile ? "justify-between pl-5 pr-1" : collapsed ? "justify-center px-1.5" : "justify-between gap-2 px-3",
        )}
      >
        {(isMobile || !collapsed || !onToggle) && (
          <Link
            href="/home"
            onClick={onNavigate}
            aria-label="Home"
            className="flex items-center rounded-md focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary/40"
          >
            <img
              src="/images/sportsbooks/upside_logo.png"
              alt="Upside"
              width={96}
              height={32}
              className="h-6 w-auto"
            />
          </Link>
        )}
        {isMobile && (
          <SheetClose
            aria-label="Close navigation"
            className="flex size-11 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <X className="size-5" />
          </SheetClose>
        )}
        {onToggle && !isMobile && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-md",
              "text-foreground/40 hover:bg-white/[0.05] hover:text-foreground/80",
              "transition-colors duration-150",
              "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary/40"
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </button>
        )}
      </div>

      <div className="relative min-h-0 flex-1">
      <nav
        aria-label="Primary"
        className={cn(
          "h-full overflow-y-auto overscroll-contain scrollbar-rail",
          isMobile ? "px-3 pt-3 pb-4" : collapsed ? "px-1.5 pt-3 pb-4" : "px-2 pt-3 pb-4"
        )}
        data-tour="sidebar-nav"
      >
        {isMobile && (
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => {
              onNavigate?.();
              openCommandPalette();
            }}
            className="mb-3 flex h-11 w-full items-center gap-2.5 rounded-lg border border-surface-line bg-white/[0.03] px-3 text-left text-[14px] text-zinc-500 transition-colors hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <Search className="size-4 shrink-0" aria-hidden="true" />
            Search tools and pages…
          </button>
        )}
        <div className={isMobile ? "mb-5" : "mb-3"}>
          <Item item={HOME_NAV_ITEM} {...itemProps} />
        </div>
        {NAV_GROUPS.map((group, index) => (
          <div key={group.label}>
            <GroupHeader label={group.label} isFirst={index === 0} collapsed={collapsed} isMobile={isMobile} />
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <div
                  key={item.href}
                  {...(item.href === "/learning-center" ? { "data-tour": "learning-center" } : {})}
                >
                  <Item item={item} {...itemProps} />
                </div>
              ))}
            </div>
          </div>
        ))}
        {isMobile && (
          <div className="mt-5 space-y-0.5 border-t border-surface-line pt-3">
            <RailUtilities variant="mobile" hasUnreadUpdates={hasUnreadUpdates} onNavigate={onNavigate} />
            <HelpMenu variant="mobile" onNavigate={onNavigate} />
          </div>
        )}
      </nav>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-surface-void to-transparent"
        )}
      />
      </div>

      {showOnboardingChecklist && (
        <Suspense fallback={null}>
          <OnboardingChecklist
            hasOnboardingPreferences={hasOnboardingPreferences}
            collapsed={collapsed}
            dismissed={checklistDismissed}
            onNavigate={onNavigate}
          />
        </Suspense>
      )}

      <div className={cn("shrink-0 border-t", isMobile ? "border-surface-line" : "border-white/[0.08]")}>
        {!isMobile && (
          <RailUtilities
            collapsed={collapsed}
            hasUnreadUpdates={hasUnreadUpdates}
            onNavigate={onNavigate}
          />
        )}

        <div className={cn(
          "flex items-center gap-1",
          isMobile ? "px-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2" : collapsed ? "flex-col px-1.5 pb-2.5 pt-1" : "px-2 pb-2.5 pt-1"
        )}>
          {!isMobile && (
            <div className={collapsed ? "w-full" : "min-w-0 flex-1"}>
              <HelpMenu collapsed={collapsed} onNavigate={onNavigate} />
            </div>
          )}
          {!isMobile && <LiveBroadcastHeader />}
          <UserMenu collapsed={collapsed} variant={variant} onNavigate={onNavigate} />
          {isMobile && <LiveBroadcastHeader />}
        </div>
      </div>
    </div>
  );
}
